import React, { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CertCatalogLookup, LookupItem } from '../types';
import { useCrewLookups } from '../useData';
import {
  RankRequirementsReport,
  type RankRequirementMatrixRow,
  type RankRequirementNatColumn,
} from './RankRequirementsReport';
import {
  rankRequirementsBackPath,
  rankRequirementsListApiUrl,
} from './rankRequirementsPrintMeta';

type RankRequirementApiRow = {
  id: number;
  rankId: number;
  vesselId?: number | null;
  certCatalogId: number;
  nationalityId: number;
  isMandatory: boolean;
  notes?: string | null;
  sortOrder: number;
  isActive: boolean;
  rank?: { id: number; name: string; abrv?: string | null } | null;
  vessel?: { id: number; vesselName: string } | null;
  certCatalog?: { id: number; name: string; categoryId?: number | null } | null;
  nationality?: { id: number; name: string; code?: string | null } | null;
};

type NatSettingResponse = { key: string; value: number[] };

const RankRequirementsReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const { lookups } = useCrewLookups();

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const rankId = searchParams.get('rankId')?.trim() ?? '';
  const listUrl = useMemo(() => rankRequirementsListApiUrl(searchParams), [searchParams]);

  const { data, error, isLoading } = useSWR<RankRequirementApiRow[]>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });
  const { data: natSetting } = useSWR<NatSettingResponse>(
    '/settings/crew/requirement-nationalities',
    fetcher,
    { revalidateOnFocus: false },
  );

  const ranks = lookups?.ranks ?? [];
  const vessels = lookups?.vessels ?? [];
  const certCatalogs = lookups?.certCatalogs ?? [];
  const certCategories = lookups?.certCategories ?? [];
  const allNationalities = lookups?.nationalities ?? [];

  const vesselName = useMemo(() => {
    const fromApi = data?.[0]?.vessel?.vesselName;
    if (fromApi) return fromApi;
    return vessels.find((v) => String(v.id) === vesselId)?.vesselName ?? `Vessel #${vesselId}`;
  }, [data, vesselId, vessels]);

  const selectedRank = useMemo(() => {
    const fromApi = data?.[0]?.rank;
    if (fromApi?.name) return fromApi;
    return ranks.find((r) => String(r.id) === rankId) ?? null;
  }, [data, rankId, ranks]);

  const categoryById = useMemo(() => {
    const map = new Map<number, LookupItem>();
    for (const c of certCategories) map.set(c.id, c);
    return map;
  }, [certCategories]);

  const certById = useMemo(() => {
    const map = new Map<number, CertCatalogLookup>();
    for (const c of certCatalogs) map.set(c.id, c);
    return map;
  }, [certCatalogs]);

  const nationalityById = useMemo(() => {
    const map = new Map<number, LookupItem>();
    for (const n of allNationalities) map.set(n.id, n);
    return map;
  }, [allNationalities]);

  const nationalities = useMemo((): RankRequirementNatColumn[] => {
    const ids = Array.isArray(natSetting?.value) ? natSetting!.value : [];
    const fromSettings = ids
      .map((id) => {
        const n = nationalityById.get(id);
        if (!n) return null;
        return { id: n.id, label: (n.code || n.name).trim() || n.name };
      })
      .filter((n): n is RankRequirementNatColumn => Boolean(n));

    if (fromSettings.length > 0) return fromSettings;

    // Fallback: nationalities that appear on mandatory Yes cells
    const seen = new Map<number, RankRequirementNatColumn>();
    for (const r of data ?? []) {
      if (!r.isMandatory || seen.has(r.nationalityId)) continue;
      const n = nationalityById.get(r.nationalityId);
      const label =
        r.nationality?.code ||
        r.nationality?.name ||
        n?.code ||
        n?.name ||
        `#${r.nationalityId}`;
      seen.set(r.nationalityId, { id: r.nationalityId, label });
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data, natSetting, nationalityById]);

  const { rows } = useMemo(() => {
    const orderOf = (n: number | null | undefined, fallback = 999_999) => {
      const v = Number(n);
      return Number.isFinite(v) ? v : fallback;
    };

    const byCert = new Map<number, RankRequirementMatrixRow>();

    for (const r of data ?? []) {
      if (!r.isMandatory) continue;
      const cert = certById.get(r.certCatalogId);
      const categoryId = cert?.categoryId ?? r.certCatalog?.categoryId ?? null;
      const catLookup = categoryId != null ? categoryById.get(categoryId) : undefined;
      const category = catLookup?.name?.trim() || 'Uncategorized';
      const certificate = r.certCatalog?.name || cert?.name || `Cert #${r.certCatalogId}`;
      const existing = byCert.get(r.certCatalogId);
      if (existing) {
        existing.byNationality[r.nationalityId] = true;
      } else {
        byCert.set(r.certCatalogId, {
          key: String(r.certCatalogId),
          category,
          certificate,
          categoryOrder: orderOf(catLookup?.sortOrder, categoryId == null ? 999_999 : 0),
          certOrder: orderOf(cert?.sortOrder),
          byNationality: { [r.nationalityId]: true },
        });
      }
    }

    const list = Array.from(byCert.values()).sort((a, b) => {
      if (a.categoryOrder !== b.categoryOrder) return a.categoryOrder - b.categoryOrder;
      if (a.certOrder !== b.certOrder) return a.certOrder - b.certOrder;
      return a.certificate.localeCompare(b.certificate, undefined, { sensitivity: 'base' });
    });

    return { rows: list };
  }, [categoryById, certById, data]);

  const backTo = rankRequirementsBackPath(searchParams);

  if (!vesselId || !rankId) {
    return <Navigate to="/parameters/crew?section=certificate-requirements" replace />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Document Requirements…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load document requirements.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Document Requirements
        </Link>
      </div>
    );
  }

  const rankName = selectedRank?.name || `Rank #${rankId}`;
  const rankAbrv = selectedRank?.abrv ?? null;
  const subtitle = `${vesselName} · ${rankAbrv || rankName}`;

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Document Requirements"
      title="Document Requirements"
      subtitle={subtitle}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <RankRequirementsReport
        rows={rows}
        nationalities={nationalities}
        vesselName={vesselName}
        rankName={rankName}
        rankAbrv={rankAbrv}
      />
    </ReportView>
  );
};

export default RankRequirementsReportPage;
